// src/pages/InventoryMovementsListPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

/** ---------- Types ---------- */

type InventoryMovement = {
  id: number;
  productCode: string;
  productName?: string | null;

  qty: number;
  qtySell: number;

  unitCost: number;
  baseUnitCost?: number | null;

  reason: string;
  refTable?: string | null;
  refNo?: string | null;

  keychainOptionId?: number | null;
  keychainLabel?: string | null;

  accessoryIdsRaw?: string | null;
  accessorySummary?: string | null;

  status?: string | null;
  createdAt: string;
};

type MovementsResponse = {
  items?: unknown;
  total?: unknown;
  limit?: unknown;
  offset?: unknown;
};

type EditAccessory = {
  keychainId: number;
  label: string;
  imageUrl?: string;
  qtyPerUnit: number;
  lastUnitCost?: number | null;
};

type KeychainOption = {
    id: number;
    label: string;
    extraCost: number;
    lastUnitCost: number | null;
    imageUrl?: string;
    category?: "KEYCHAIN" | "ACCESSORY" | string;
    stockOnHand?: number | null; // 👈 เพิ่ม
  };

const INVENTORY_MOVEMENTS_API = apiUrl("/inventory_movements_list");
const KEYCHAIN_OPTIONS_API = apiUrl("/keychain_options_list");
const PAGE_SIZE = 50;

/** ---------- Small helpers ---------- */

function toNumber(val: unknown, fallback = 0): number {
  if (typeof val === "number") return Number.isFinite(val) ? val : fallback;
  if (typeof val === "string") {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function toStringOrNull(val: unknown): string | null {
  if (typeof val === "string") return val;
  return null;
}

function safeJsonParse<T>(raw: unknown): T | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as T;
    return parsed;
  } catch {
    return null;
  }
}

/** accessory_ids: JSON ที่เราเคยเก็บจาก backend
 *  รูปแบบ (โดยประมาณ):
 * [
 *   {
 *     "keychain_id": 5,
 *     "qtyPerUnit": 1,
 *     "lots": [
 *       { "keychain_movement_id": 12, "unitCost": 7, "used": 2 },
 *       ...
 *     ]
 *   },
 *   ...
 * ]
 */
function parseAccessoriesFromRaw(raw: string | null | undefined): EditAccessory[] {
  if (!raw) return [];
  type RawLot = { unitCost?: unknown; used?: unknown; keychain_movement_id?: unknown };
  type RawAcc = {
    keychain_id?: unknown;
    keychainId?: unknown;
    qtyPerUnit?: unknown;
    lots?: RawLot[];
  };

  const arr = safeJsonParse<RawAcc[]>(raw);
  if (!arr || !Array.isArray(arr)) return [];

  const results: EditAccessory[] = [];

  for (const a of arr) {
    const keychainId = toNumber(a.keychain_id ?? a.keychainId, NaN);
    if (!Number.isFinite(keychainId) || keychainId <= 0) continue;

    const qtyPerUnit = toNumber(a.qtyPerUnit, 1) || 1;
    let lastUnitCost: number | null = null;

    if (Array.isArray(a.lots) && a.lots.length > 0) {
      const firstLot = a.lots[0];
      const u = toNumber(firstLot.unitCost, NaN);
      if (Number.isFinite(u)) lastUnitCost = u;
    }

    results.push({
      keychainId,
      label: `ID ${keychainId}`,
      imageUrl: undefined,
      qtyPerUnit,
      lastUnitCost,
    });
  }

  return results;
}

/** แปลง accessory_ids เป็นข้อความสรุปสั้น ๆ ไว้โชว์ใน table */
function computeAccessorySummary(raw: string | null | undefined): string | null {
  const accs = parseAccessoriesFromRaw(raw);
  if (!accs.length) return null;
  if (accs.length === 1) {
    const a = accs[0];
    return `อะไหล่ ID ${a.keychainId} × ${a.qtyPerUnit}/ชิ้น`;
  }
  return `${accs.length} ประเภทอะไหล่`;
}

/** คำนวนต้นทุนอะไหล่รวมต่อ 1 ชิ้นจาก accessory_ids */
function computeAccessoryCostPerUnit(
    raw: string | null | undefined
  ): number {
    const accs = parseAccessoriesFromRaw(raw);
    return accs.reduce((sum, a) => {
      const u = a.lastUnitCost;
      if (u == null) return sum;
      return sum + a.qtyPerUnit * u;
    }, 0);
  }

/** ---------- Component ---------- */

export default function InventoryMovementsListPage(): JSX.Element {
  /** ---------- UI State ---------- */
  const [items, setItems] = useState<InventoryMovement[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // filter
  const [productCodeFilter, setProductCodeFilter] = useState<string>("");
  const [reasonFilter, setReasonFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // edit modal
  const [editing, setEditing] = useState<InventoryMovement | null>(null);
  const [editQty, setEditQty] = useState<number>(0);
  const [editUnitCost, setEditUnitCost] = useState<number>(0);
  const [editReason, setEditReason] = useState<string>("PURCHASE");
  const [editRefNo, setEditRefNo] = useState<string>("");

  // 👇 ใหม่: เก็บ base_unit_cost
    const [editBaseUnitCost, setEditBaseUnitCost] = useState<number | null>(null);



    // keychain + accessories
    const [editKeychainId, setEditKeychainId] = useState<number | null>(null);
    const [editAccessories, setEditAccessories] = useState<EditAccessory[]>([]);
    const [keychainOptions, setKeychainOptions] = useState<KeychainOption[]>([]);
    // modal เลือกพวงกุญแจ
    const [showKeychainModal, setShowKeychainModal] = useState<boolean>(false);
    const [keychainQuery, setKeychainQuery] = useState<string>("");

    // modal เลือกเครื่องประดับ
    const [showAccessoryModal, setShowAccessoryModal] = useState<boolean>(false);
    const [accessoryQuery, setAccessoryQuery] = useState<string>("");

    const filteredKeychainOptions = useMemo(
    () =>
        keychainOptions
        .filter((k) => (k.category ?? "KEYCHAIN") === "KEYCHAIN")
        .filter((k) =>
            keychainQuery.trim()
            ? k.label.toLowerCase().includes(keychainQuery.toLowerCase())
            : true
        ),
    [keychainOptions, keychainQuery]
    );

    const filteredAccessoryOptions = useMemo(
    () =>
        keychainOptions
        .filter((k) => k.category === "ACCESSORY")
        .filter((k) =>
            accessoryQuery.trim()
            ? k.label.toLowerCase().includes(accessoryQuery.toLowerCase())
            : true
        ),
    [keychainOptions, accessoryQuery]
    );

    // helper เลือกพวงกุญแจ
    function chooseKeychain(opt: KeychainOption): void {
    setEditKeychainId(opt.id);
    setShowKeychainModal(false);
    }

    // helper เพิ่มเครื่องประดับ
    function addAccessoryFromOption(opt: KeychainOption): void {
    setEditAccessories((prev) => {
        if (prev.some((a) => a.keychainId === opt.id)) return prev;
        return [
        ...prev,
        {
            keychainId: opt.id,
            label: opt.label,
            imageUrl: opt.imageUrl,
            qtyPerUnit: 1,
            lastUnitCost: opt.lastUnitCost ?? opt.extraCost ?? 0,
        },
        ];
    });
    setShowAccessoryModal(false);
    }

  const pageCount = useMemo(() => {
    if (PAGE_SIZE <= 0) return 1;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [total]);

  // ✅ ต้นทุนเพิ่มจาก "พวงกุญแจหลัก" ต่อ 1 ชิ้น
  const keychainExtraCost = useMemo(() => {
    if (editKeychainId == null) return 0;
    const opt = keychainOptions.find((k) => k.id === editKeychainId);
    if (!opt) return 0;
    return opt.lastUnitCost != null ? opt.lastUnitCost : 0;
  }, [editKeychainId, keychainOptions]);

  // ✅ ต้นทุนเพิ่มจาก "อะไหล่/เครื่องประดับทั้งหมด" ต่อ 1 ชิ้น
  const accessoriesExtraCost = useMemo(
    () =>
      editAccessories.reduce((sum, a) => {
        if (a.lastUnitCost == null) return sum;
        return sum + a.qtyPerUnit * a.lastUnitCost;
      }, 0),
    [editAccessories]
  );

  useEffect(() => {
    void loadKeychainOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ ต้นทุนรวมต่อชิ้น (ฐาน + พวงกุญแจ + อะไหล่)
  const totalEstimatedUnitCost = useMemo(
    () => (editBaseUnitCost ?? 0) + keychainExtraCost + accessoriesExtraCost,
    [editBaseUnitCost, keychainExtraCost, accessoriesExtraCost]
  );

  /** ---------- Effects ---------- */

  useEffect(() => {
    void fetchMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, productCodeFilter, reasonFilter, dateFrom, dateTo]);

  /** ---------- Load keychain options ---------- */

  async function loadKeychainOptions(): Promise<void> {
    try {
      const resp = await fetch(KEYCHAIN_OPTIONS_API);
      if (!resp.ok) throw new Error("failed_to_fetch_keychain_options");
      const data = (await resp.json()) as { items?: unknown };
  
      const list: KeychainOption[] = Array.isArray(data.items)
        ? data.items.map((r: unknown) => {
            const row = r as Record<string, unknown>;
            return {
                id: toNumber(row.id, 0),
                label: toStringOrNull(row.label) ?? "",
                imageUrl: toStringOrNull(row.imageUrl) ?? undefined,
                extraCost: toNumber(row.extra_cost, 0),
                lastUnitCost:
                row.last_unit_cost != null
                    ? toNumber(row.last_unit_cost, 0)
                    : null,
                category:
                (toStringOrNull(row.category) as KeychainOption["category"]) ??
                "KEYCHAIN",
                stockOnHand:
                row.stock_on_hand != null
                    ? toNumber(row.stock_on_hand, 0)
                    : null, // 👈 เพิ่ม
            };
            })
        : [];
  
      setKeychainOptions(list);
    } catch (err) {
      console.error("loadKeychainOptions error:", err);
    }
  }

  /** ---------- Fetch list ---------- */

  async function fetchMovements(): Promise<void> {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * PAGE_SIZE;

      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));

      // filter กลายเป็น query param (ถ้า backend ยังไม่รองรับก็จะถูกเมินไปเฉย ๆ)
      if (productCodeFilter.trim() !== "") {
        params.set("product_code", productCodeFilter.trim());
      }
      if (reasonFilter.trim() !== "") {
        params.set("reason", reasonFilter.trim());
      }
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);

      const resp = await fetch(`${INVENTORY_MOVEMENTS_API}?${params.toString()}`, {
        method: "GET",
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(text || "failed_to_fetch_inventory_movements");
      }

      const data = (await resp.json()) as MovementsResponse;

      const rawItems = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data)
        ? (data as unknown[])
        : [];

        const mapped: InventoryMovement[] = rawItems.map((r: unknown) => {
            const row = r as Record<string, unknown>;
          
            const productCode = toStringOrNull(row.product_code) ?? "";
            const productName = toStringOrNull(row.product_name);
          
            // 👇 รองรับทั้งกรณีเป็น string และเป็น JSON ที่ถูก parse แล้ว
            const accessoryField = row.accessory_ids;
            const accessoryIdsRaw =
              typeof accessoryField === "string"
                ? accessoryField
                : accessoryField != null
                ? JSON.stringify(accessoryField)
                : null;
          
            return {
              id: toNumber(row.id, 0),
              productCode,
              productName,
          
              qty: toNumber(row.qty, 0),
              qtySell: toNumber(row.qty_sell, 0),
          
              unitCost: toNumber(row.unit_cost, 0),
              baseUnitCost:
                row.base_unit_cost != null ? toNumber(row.base_unit_cost, 0) : null,
          
              reason: toStringOrNull(row.reason) ?? "",
              refTable: toStringOrNull(row.ref_table),
              refNo: toStringOrNull(row.ref_no),
          
              keychainOptionId:
                row.keychain_option_id != null
                  ? toNumber(row.keychain_option_id, 0)
                  : null,
              keychainLabel: toStringOrNull(row.keychain_label),
          
              accessoryIdsRaw,
              accessorySummary: computeAccessorySummary(accessoryIdsRaw),
          
              status: toStringOrNull(row.status),
              createdAt: toStringOrNull(row.created_at) ?? "",
            };
          });

      setItems(mapped);

      // ถ้า backend ส่ง total มาให้ก็ใช้เลย, ถ้าไม่มีก็เดาแบบง่าย ๆ
      const totalFromServer = toNumber(data.total, -1);
      if (totalFromServer >= 0) {
        setTotal(totalFromServer);
      } else {
        const approx = (page - 1) * PAGE_SIZE + mapped.length;
        setTotal(approx);
      }
    } catch (err) {
      console.error("fetchMovements error:", err);
      const msg =
        err instanceof Error ? err.message : "failed_to_fetch_inventory_movements";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  /** ---------- Helpers ---------- */

  function formatDateTime(dt: string): string {
    if (!dt) return "-";
    try {
      const d = new Date(dt);
      if (Number.isNaN(d.getTime())) return dt;
      return d.toLocaleString("th-TH", {
        timeZone: "Asia/Bangkok",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dt;
    }
  }

  function reasonLabel(reason: string): string {
    switch (reason) {
      case "PURCHASE":
        return "ซื้อเข้า";
      case "SALE":
        return "ขายออก";
      case "ADJUST":
        return "ปรับสต็อก";
      default:
        return reason;
    }
  }

  function canEdit(m: InventoryMovement): boolean {
    // ตอนนี้ทำแบบ conservative: ถ้ามีขายแล้วห้ามแก้
    if (m.qtySell > 0) return false;
    return true;
  }

  function canDelete(m: InventoryMovement): boolean {
    if (m.qtySell > 0) return false;
    if (m.keychainOptionId != null) return false;
  
    // accessoryIdsRaw = null → ลบได้
    // accessoryIdsRaw = [] → ลบได้
    // accessoryIdsRaw = [1,2] → ลบไม่ได้
    if (Array.isArray(m.accessoryIdsRaw) && m.accessoryIdsRaw.length > 0) {
      return false;
    }
  
    return true;
  }

  useEffect(() => {
    setEditUnitCost(totalEstimatedUnitCost);
  }, [totalEstimatedUnitCost]);

  /** ---------- Edit Modal ---------- */

  function openEdit(m: InventoryMovement): void {
    setEditing(m);
    setEditQty(m.qty);
    setEditUnitCost(m.unitCost);
    setEditReason(m.reason);
    setEditRefNo(m.refNo ?? "");

    // 👇 ดึงฐานจาก m.baseUnitCost
    setEditBaseUnitCost(
        m.baseUnitCost != null ? Number(m.baseUnitCost) : null
    );

    setEditKeychainId(
      m.keychainOptionId != null ? Number(m.keychainOptionId) : null
    );

    // โหลด keychain options ถ้ายังไม่ได้โหลด
    if (keychainOptions.length === 0) {
      void loadKeychainOptions();
    }

    // parse accessories จาก accessory_ids (JSON) ที่ backend เคยเก็บ
    const parsed = parseAccessoriesFromRaw(m.accessoryIdsRaw);
    // enrich ด้วย label + image จาก keychainOptions (ถ้ามีอยู่แล้ว)
    const enriched: EditAccessory[] = parsed.map((a) => {
      const opt = keychainOptions.find((k) => k.id === a.keychainId);
      if (!opt) return a;
      return {
        ...a,
        label: opt.label,
        imageUrl: opt.imageUrl,
        lastUnitCost:
          opt.lastUnitCost != null ? opt.lastUnitCost : a.lastUnitCost,
      };
    });

    setEditAccessories(enriched);
  }

  function closeEdit(): void {
    setEditing(null);
    setEditAccessories([]);
    setEditKeychainId(null);
  }

  async function handleSaveEdit(): Promise<void> {
    if (!editing) return;

    // PMN-CRITICAL(/InventoryMovementsListPage): editing a movement can rewrite lot qty/cost and consume/return keychain/accessory FIFO stock in backend.
    const id = editing.id;
    const oldKeychainOptionId = editing.keychainOptionId ?? null;

    if (!Number.isFinite(editQty) || editQty <= 0) {
      alert("จำนวนต้องมากกว่า 0");
      return;
    }

    // 👇 ใช้ค่าที่คำนวณจากฐาน + พวงกุญแจ + อะไหล่
    const newUnitCost = totalEstimatedUnitCost;
    
    if (!Number.isFinite(editUnitCost) || editUnitCost < 0) {
      alert("ต้นทุนต่อชิ้นต้องไม่ติดลบ");
      return;
    }

    const accessoriesPayload = editAccessories.map((a) => ({
      keychain_id: a.keychainId,
      qtyPerUnit: a.qtyPerUnit,
    }));

    try {
        setLoading(true);
        setError(null);
  
        const resp = await fetch(apiUrl(`/inventory_movements/${id}`), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              qty: editQty,
              unit_cost: newUnitCost,
              base_unit_cost: editBaseUnitCost ?? 0,
              reason: editReason,
              ref_no: editRefNo.trim() || "-",
              keychain_option_id: editKeychainId,        // ค่าใหม่ (อาจจะเป็น null)
              old_keychain_option_id: oldKeychainOptionId, // ค่าเดิม
              accessories: accessoriesPayload,
              use_keychain_stock: true, // 👈 เปิดโหมดจัดการ stock พวงกุญแจหลัก
            }),
          });
  
        if (!resp.ok) {
          const payload = await resp.json().catch(() => ({}));
          const msg =
            (payload && (payload.error as string | undefined)) ||
            "failed_to_update_inventory_movement";
          throw new Error(msg);
        }
  
        closeEdit();
        await fetchMovements();
        await loadKeychainOptions();   // 👈 เพิ่มบรรทัดนี้
      } catch (err) {
        console.error("handleSaveEdit error:", err);
        const msg =
          err instanceof Error ? err.message : "failed_to_update_inventory_movement";
        setError(msg);
      } finally {
        setLoading(false);
      }
  }

  /** ---------- Delete ---------- */

  async function handleDelete(m: InventoryMovement): Promise<void> {
    // PMN-CRITICAL(/InventoryMovementsListPage): deleting an inventory movement removes a real stock lot; keep frontend guard aligned with backend DELETE checks.
    if (!canDelete(m)) {
      alert("รายการนี้ลบไม่ได้ (มีการขายแล้ว)");
      return;
    }

    const ok = window.confirm(
      `ยืนยันลบ movement #${m.id} ของรหัส ${m.productCode} จำนวน ${m.qty} ชิ้น ?`
    );
    if (!ok) return;

    try {
      setLoading(true);
      setError(null);

      const resp = await fetch(apiUrl(`/inventory_movements/${m.id}`), {
        method: "DELETE",
      });

      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        const msg =
          (payload && (payload.error as string | undefined)) ||
          "delete_inventory_movement_failed";
        throw new Error(msg);
      }

      await fetchMovements();
    } catch (err) {
      console.error("handleDelete error:", err);
      const msg =
        err instanceof Error ? err.message : "delete_inventory_movement_failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  /** ---------- Render ---------- */

  return (
    <div className="container py-4">
      <div className="mb-3">
        <h2 className="h4 mb-1">ประวัติการเคลื่อนไหวสต็อก (Inventory Movements)</h2>
        <div className="text-muted small">
          ดูย้อนหลังทุก movement และจัดการ แก้ไข / ลบ พร้อมพวงกุญแจและอะไหล่
        </div>
      </div>

      {/* Filter Card */}
      <div className="card shadow border-0 rounded-4 mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label form-label-sm">รหัสสินค้า</label>
              <input
                className="form-control form-control-sm"
                value={productCodeFilter}
                onChange={(e) => {
                  setPage(1);
                  setProductCodeFilter(e.target.value);
                }}
                placeholder="เช่น PD123"
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label form-label-sm">ประเภท movement</label>
              <select
                className="form-select form-select-sm"
                value={reasonFilter}
                onChange={(e) => {
                  setPage(1);
                  setReasonFilter(e.target.value);
                }}
              >
                <option value="">ทั้งหมด</option>
                <option value="PURCHASE">ซื้อเข้า (PURCHASE)</option>
                <option value="SALE">ขายออก (SALE)</option>
                <option value="ADJUST">ปรับสต็อก (ADJUST)</option>
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label form-label-sm">จากวันที่</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFrom}
                onChange={(e) => {
                  setPage(1);
                  setDateFrom(e.target.value);
                }}
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label form-label-sm">ถึงวันที่</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateTo}
                onChange={(e) => {
                  setPage(1);
                  setDateTo(e.target.value);
                }}
              />
            </div>

            <div className="col-12 col-md-2 text-md-end mt-2 mt-md-0">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={() => {
                  setProductCodeFilter("");
                  setReasonFilter("");
                  setDateFrom("");
                  setDateTo("");
                  setPage(1);
                }}
              >
                ล้างตัวกรอง
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => {
                  setPage(1);
                  void fetchMovements();
                  void loadKeychainOptions();
                }}
              >
                รีเฟรช
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error / Loading */}
      {error && (
        <div className="alert alert-danger py-2">
          เกิดข้อผิดพลาด:{" "}
          <span className="fw-monospace small">{error}</span>
        </div>
      )}
      {loading && (
        <div className="alert alert-info py-2">กำลังโหลดข้อมูล...</div>
      )}

      {/* Table */}
      <div className="card shadow border-0 rounded-4">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                <tr>
                    <th className="text-center" style={{ width: "60px" }}>
                    ID
                    </th>
                    <th>สินค้า</th>
                    <th className="text-end" style={{ width: "90px" }}>
                    จำนวน
                    </th>
                    <th className="text-end" style={{ width: "110px" }}>
                    ขายแล้ว
                    </th>
                    {/* ต้นทุนรวมต่อชิ้น (เก็บใน unit_cost ปัจจุบัน) */}
                    <th className="text-end" style={{ width: "120px" }}>
                    ต้นทุน/ชิ้น (รวม)
                    </th>
                    {/* ใหม่: base_unit_cost */}
                    <th className="text-end" style={{ width: "120px" }}>
                    ต้นทุนฐาน
                    </th>
                    {/* ใหม่: ต้นทุนอะไหล่จาก accessory_ids */}
                    <th className="text-end" style={{ width: "120px" }}>
                    ต้นทุนอะไหล่
                    </th>
                    <th style={{ width: "220px" }}>พวงกุญแจ / อะไหล่</th>
                    <th className="text-center" style={{ width: "150px" }}>
                    จัดการ
                    </th>
                </tr>
                </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      ไม่พบข้อมูล movement
                    </td>
                  </tr>
                )}

                {items.map((m) => {
                const disabledEdit = !canEdit(m);
                const disabledDelete = !canDelete(m);

                const accessoryCostPerUnit = computeAccessoryCostPerUnit(
                    m.accessoryIdsRaw
                );

                return (
                    <tr key={m.id}>
                    <td className="text-center">{m.id}</td>
                    <td>
                        <div className="fw-semibold">{m.productCode}</div>
                        {m.productName && (
                        <div className="small text-muted">{m.productName}</div>
                        )}
                    </td>
                    <td className="text-end">{m.qty}</td>
                    <td className="text-end text-muted">{m.qtySell}</td>

                    {/* ต้นทุนรวมต่อชิ้น (unit_cost เดิม) */}
                    <td className="text-end">
                        {m.unitCost.toLocaleString("th-TH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                        })}
                    </td>

                    {/* ใหม่: base_unit_cost */}
                    <td className="text-end">
                        {m.baseUnitCost != null
                        ? m.baseUnitCost.toLocaleString("th-TH", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            })
                        : "-"}
                    </td>

                    {/* ใหม่: ต้นทุนอะไหล่รวมต่อชิ้นจาก accessory_ids */}
                    <td className="text-end">
                        {accessoryCostPerUnit > 0
                        ? accessoryCostPerUnit.toLocaleString("th-TH", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            })
                        : "-"}
                    </td>

                    <td className="small">
                        {m.keychainOptionId != null && (
                        <div>
                            พวงกุญแจ:{" "}
                            {m.keychainLabel
                            ? `${m.keychainLabel} (#${m.keychainOptionId})`
                            : `ID ${m.keychainOptionId}`}
                        </div>
                        )}
                        {m.accessorySummary && (
                        <div className="text-muted">{m.accessorySummary}</div>
                        )}
                    </td>

                    <td className="text-center">
                        <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-1"
                        disabled={disabledEdit}
                        onClick={() => openEdit(m)}
                        >
                        แก้ไข
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={disabledDelete}
                            title={
                                disabledDelete
                                ? m.qtySell > 0
                                    ? "ลบไม่ได้: มีการขายออกแล้ว"
                                    : "ลบไม่ได้: ผูกพวงกุญแจ/อะไหล่ไว้"
                                : ""
                            }
                            onClick={() => void handleDelete(m)}
                            >
                            ลบ
                        </button>
                    </td>
                    </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
            <div className="small text-muted">
              ทั้งหมด {total.toLocaleString("th-TH")} รายการ • หน้า {page} /{" "}
              {pageCount}
            </div>
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ก่อนหน้า
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={items.length < PAGE_SIZE}
                onClick={() =>
                  setPage((p) =>
                    items.length < PAGE_SIZE ? p : p + 1
                  )
                }
              >
                ถัดไป
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex={-1}
            role="dialog"
          >
            <div
              className="modal-dialog modal-xl modal-dialog-centered"
              role="document"
            >
              <div className="modal-content rounded-4">
                <div className="modal-header">
                  <h5 className="modal-title">
                    แก้ไข movement #{editing.id} ({editing.productCode})
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeEdit}
                  />
                </div>

                <div className="modal-body">
                  {/* แถวบน: qty / unit_cost / reason / ref_no */}
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-3">
                      <label className="form-label form-label-sm">
                        จำนวน (qty)
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={editQty}
                        min={1}
                        onChange={(e) =>
                          setEditQty(Number(e.target.value || 0))
                        }
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label form-label-sm">
                        ต้นทุนต่อชิ้น (unit_cost)
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={editUnitCost}
                        min={0}
                        step={0.01}
                        onChange={(e) =>
                          setEditUnitCost(Number(e.target.value || 0))
                        }
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label form-label-sm">
                        ประเภท movement
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                      >
                        <option value="PURCHASE">PURCHASE (ซื้อเข้า)</option>
                        <option value="SALE">SALE (ขายออก)</option>
                        <option value="ADJUST">ADJUST (ปรับสต็อก)</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label form-label-sm">Ref No</label>
                      <input
                        className="form-control form-control-sm"
                        value={editRefNo}
                        onChange={(e) => setEditRefNo(e.target.value)}
                        placeholder="เช่น lot / หมายเหตุเฉพาะ"
                      />
                    </div>
                  </div>

                  {/* พวงกุญแจหลัก */}
                  <hr />
                  <h6 className="mb-2">พวงกุญแจหลัก (Keychain Option)</h6>
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label form-label-sm">เลือกพวงกุญแจ</label>
                        <div className="d-flex gap-2">
                            <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                                if (keychainOptions.length === 0) void loadKeychainOptions();
                                setShowKeychainModal(true);
                            }}
                            >
                            เลือกจากรายการ...
                            </button>
                            <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setEditKeychainId(null)}
                            >
                            ไม่ใช้พวงกุญแจ
                            </button>
                        </div>
                        <div className="form-text small text-muted">
                            * เลือกพวงกุญแจที่จะใช้ต่อ 1 ชิ้นสินค้า
                        </div>
                    </div>

                    <div className="col-12 col-md-6 d-flex align-items-center">
                      {(() => {
                        const current =
                          editKeychainId != null
                            ? keychainOptions.find(
                                (k) => k.id === editKeychainId
                              )
                            : null;
                        if (!current) {
                          return (
                            <div className="text-muted small">
                              ยังไม่เลือกพวงกุญแจ หรือไม่มีรูป
                            </div>
                          );
                        }
                        return (
                            <div className="d-flex align-items-center gap-2">
                              {current.imageUrl ? (
                                <img
                                  src={current.imageUrl}
                                  alt={current.label}
                                  style={{
                                    width: 60,
                                    height: 60,
                                    objectFit: "cover",
                                  }}
                                  className="border rounded"
                                />
                              ) : (
                                <div
                                  className="border rounded d-flex align-items-center justify-content-center text-muted"
                                  style={{ width: 60, height: 60 }}
                                >
                                  ไม่มีรูป
                                </div>
                              )}
                      
                              <div className="small">
                                <div className="fw-semibold">{current.label}</div>
                                <div className="text-muted">
                                  ต้นทุนเพิ่มต่อชิ้น:{" "}
                                  {current.lastUnitCost != null
                                    ? current.lastUnitCost.toLocaleString("th-TH", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })
                                    : "-"}
                                </div>
                              </div>
                      
                              {/* 👇 ปุ่มเอาพวงกุญแจออก */}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ms-2"
                                onClick={() => setEditKeychainId(null)}
                              >
                                เอาออก
                              </button>
                            </div>
                          );
                      })()}
                    </div>
                  </div>

                  {/* เครื่องประดับ / อะไหล่ */}
                  <hr />
                  <h6 className="mb-2">เครื่องประดับ / อะไหล่ เพิ่มเติม</h6>

                    <div className="mb-2">
                        <label className="form-label form-label-sm">เพิ่มเครื่องประดับ</label>
                        <div>
                            <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                                if (keychainOptions.length === 0) void loadKeychainOptions();
                                setShowAccessoryModal(true);
                            }}
                            >
                            เลือกจากรายการ...
                            </button>
                        </div>
                        <div className="form-text small text-muted">
                            * เลือกจากรายการ ACCESSORY (ไม่ใช่พวงกุญแจ)
                        </div>
                    </div>
                    {/* -------- Modal: เลือกพวงกุญแจ -------- */}
                    {showKeychainModal && (
                    <div
                        className="modal fade show"
                        role="dialog"
                        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                        aria-modal="true"
                        aria-labelledby="keychainModalLabel"
                        onClick={(e) => {
                        if (e.target === e.currentTarget) setShowKeychainModal(false);
                        }}
                    >
                        <div className="modal-dialog modal-md modal-dialog-scrollable">
                        <div className="modal-content rounded-4 shadow">
                            <div className="modal-header">
                            <h5 className="modal-title" id="keychainModalLabel">
                                เลือกพวงกุญแจ
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={() => setShowKeychainModal(false)}
                            />
                            </div>

                            <div className="modal-body">
                            <div className="input-group mb-3">
                                <span className="input-group-text">ค้นหา</span>
                                <input
                                type="text"
                                className="form-control"
                                placeholder="พิมพ์ชื่อพวงกุญแจ"
                                value={keychainQuery}
                                onChange={(e) => setKeychainQuery(e.target.value)}
                                autoFocus
                                />
                            </div>

                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                    <th style={{ width: 64 }}>รูป</th>
                                    <th>ตัวเลือก</th>
                                    <th className="text-center" style={{ width: 80 }}>
                                        คงเหลือ
                                    </th>
                                    <th className="text-end" style={{ width: 140 }}>
                                        ต้นทุนเพิ่มต่อชิ้น
                                    </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredKeychainOptions.map((k) => {
                                    const disabled =
                                        k.stockOnHand != null && k.stockOnHand <= 0;

                                    return (
                                        <tr
                                        key={k.id}
                                        role="button"
                                        onClick={() => {
                                            if (!disabled) chooseKeychain(k);
                                        }}
                                        className={
                                            "user-select-none " +
                                            (disabled ? "opacity-50" : "table-row-hover")
                                        }
                                        style={{
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            pointerEvents: disabled ? "none" : "auto",
                                        }}
                                        >
                                        <td>
                                            {k.imageUrl ? (
                                            <img
                                                src={k.imageUrl}
                                                alt={k.label}
                                                width={40}
                                                height={40}
                                                style={{
                                                objectFit: "cover",
                                                borderRadius: 8,
                                                background: "#f5f5f5",
                                                }}
                                            />
                                            ) : (
                                            <div
                                                className="bg-light border rounded"
                                                style={{ width: 40, height: 40 }}
                                            />
                                            )}
                                        </td>
                                        <td>{k.label}</td>
                                        <td className="text-center">
                                            {k.stockOnHand != null
                                            ? k.stockOnHand.toLocaleString("th-TH")
                                            : "-"}
                                        </td>
                                        <td className="text-end">
                                            {(k.lastUnitCost ?? k.extraCost).toFixed(2)} บาท
                                        </td>
                                        </tr>
                                    );
                                    })}
                                </tbody>
                                </table>
                            </div>
                            </div>

                            <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowKeychainModal(false)}
                            >
                                ปิดหน้าต่าง
                            </button>
                            </div>
                        </div>
                        </div>
                    </div>
                    )}

                    {/* -------- Modal: เลือกเครื่องประดับ -------- */}
                    {showAccessoryModal && (
                    <div
                        className="modal fade show"
                        role="dialog"
                        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                        aria-modal="true"
                        aria-labelledby="accessoryModalLabel"
                        onClick={(e) => {
                        if (e.target === e.currentTarget) setShowAccessoryModal(false);
                        }}
                    >
                        <div className="modal-dialog modal-md modal-dialog-scrollable">
                        <div className="modal-content rounded-4 shadow">
                            <div className="modal-header">
                            <h5 className="modal-title" id="accessoryModalLabel">
                                เลือกเครื่องประดับ / อะไหล่
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={() => setShowAccessoryModal(false)}
                            />
                            </div>

                            <div className="modal-body">
                            <div className="input-group mb-3">
                                <span className="input-group-text">ค้นหา</span>
                                <input
                                type="text"
                                className="form-control"
                                placeholder="พิมพ์ชื่อเครื่องประดับ"
                                value={accessoryQuery}
                                onChange={(e) => setAccessoryQuery(e.target.value)}
                                autoFocus
                                />
                            </div>

                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 64 }}>รูป</th>
                                            <th>รายการ</th>
                                            <th className="text-center" style={{ width: 80 }}>
                                            คงเหลือ
                                            </th>
                                            <th className="text-end" style={{ width: 140 }}>
                                            ต้นทุน/ชิ้น
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {filteredAccessoryOptions.map((k) => {
                                        const disabled =
                                        k.stockOnHand != null && k.stockOnHand <= 0;

                                        return (
                                        <tr
                                            key={k.id}
                                            role="button"
                                            className={
                                            "user-select-none " +
                                            (disabled ? "opacity-50" : "table-row-hover")
                                            }
                                            style={{
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            pointerEvents: disabled ? "none" : "auto",
                                            }}
                                            onClick={() => {
                                            if (!disabled) addAccessoryFromOption(k);
                                            }}
                                        >
                                            <td>
                                            {k.imageUrl ? (
                                                <img
                                                src={k.imageUrl}
                                                alt={k.label}
                                                width={40}
                                                height={40}
                                                style={{
                                                    objectFit: "cover",
                                                    borderRadius: 8,
                                                    background: "#f5f5f5",
                                                }}
                                                />
                                            ) : (
                                                <div
                                                className="bg-light border rounded"
                                                style={{ width: 40, height: 40 }}
                                                />
                                            )}
                                            </td>
                                            <td>{k.label}</td>
                                            <td className="text-center">
                                            {k.stockOnHand != null
                                                ? k.stockOnHand.toLocaleString("th-TH")
                                                : "-"}
                                            </td>
                                            <td className="text-end">
                                            {(k.lastUnitCost ?? k.extraCost).toFixed(2)} บาท
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                            </div>

                            <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowAccessoryModal(false)}
                            >
                                ปิดหน้าต่าง
                            </button>
                            </div>
                        </div>
                        </div>
                    </div>
                    )}

                  {editAccessories.length === 0 && (
                    <div className="text-muted small">
                      ยังไม่ได้เลือกเครื่องประดับ
                    </div>
                  )}

                  {editAccessories.map((a, idx) => (
                    <div
                      key={a.keychainId}
                      className="border rounded p-2 mb-2 d-flex align-items-center gap-2"
                    >
                      {a.imageUrl ? (
                        <img
                          src={a.imageUrl}
                          alt={a.label}
                          style={{
                            width: 50,
                            height: 50,
                            objectFit: "cover",
                          }}
                          className="border rounded"
                        />
                      ) : (
                        <div
                          className="border rounded d-flex align-items-center justify-content-center text-muted"
                          style={{ width: 50, height: 50 }}
                        >
                          ไม่มีรูป
                        </div>
                      )}

                      <div className="flex-grow-1">
                        <div className="fw-semibold">{a.label}</div>
                        <div className="d-flex align-items-center gap-2 small mt-1">
                          <span>จำนวนต่อชิ้นสินค้า:</span>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: 80 }}
                            min={0}
                            step={1}
                            value={a.qtyPerUnit}
                            onChange={(e) => {
                              const v = Number(e.target.value || 0);
                              setEditAccessories((prev) => {
                                const cp = [...prev];
                                cp[idx] = { ...cp[idx], qtyPerUnit: v };
                                return cp;
                              });
                            }}
                          />
                          <span>
                            × ต้นทุน/ชิ้น:{" "}
                            {a.lastUnitCost != null
                              ? a.lastUnitCost.toLocaleString("th-TH", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : "-"}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          setEditAccessories((prev) =>
                            prev.filter((_, i) => i !== idx)
                          );
                        }}
                      >
                        ลบ
                      </button>
                    </div>
                  ))}
{/* สรุปต้นทุนต่อชิ้นหลังการเลือกพวงกุญแจ / อะไหล่ */}
<hr />
                  <h6 className="mb-2">
                    สรุปต้นทุนต่อชิ้น (ประเมินหลังเลือกพวงกุญแจ / เครื่องประดับ)
                  </h6>

                  <div className="row g-2 mb-2">
                    <div className="col-12 col-md-4">
                        <div className="small text-muted">ต้นทุนสินค้า (ฐาน)</div>
                            <div className="fs-6 fw-semibold">
                                {(editBaseUnitCost ?? 0).toLocaleString("th-TH", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                })}
                            </div>
                        </div>

                    <div className="col-12 col-md-4">
                      <div className="small text-muted">เพิ่มจากพวงกุญแจ</div>
                      <div className="fs-6 fw-semibold">
                        {keychainExtraCost.toLocaleString("th-TH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="small text-muted">เพิ่มจากอะไหล่รวม</div>
                      <div className="fs-6 fw-semibold">
                        {accessoriesExtraCost.toLocaleString("th-TH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 p-2 bg-light rounded-3">
                    <div className="small text-muted">
                      ต้นทุนรวมต่อชิ้น (ประเมิน)
                    </div>
                    <div className="fs-5 fw-bold">
                      {totalEstimatedUnitCost.toLocaleString("th-TH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  <div className="small text-muted mt-2">
                    * การแก้ไขพวงกุญแจ/อะไหล่
                    ตรงนี้จะส่งข้อมูลไปให้ backend ที่เส้น
                    <code>PUT /inventory_movements/:id</code>{" "}
                    ซึ่งต้องไปเขียน logic คืน stock / FIFO
                    ให้เรียบร้อยฝั่ง server
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={closeEdit}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => void handleSaveEdit()}
                  >
                    บันทึกการแก้ไข
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Backdrop */}
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
